import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "add-package-modal-body" }
const _hoisted_2 = { class: "modal-bar" }
const _hoisted_3 = { class: "section-title" }
const _hoisted_4 = { class: "pricing-list" }
const _hoisted_5 = { class: "pricing-row" }
const _hoisted_6 = { class: "pricing-field" }
const _hoisted_7 = { class: "pricing-field" }
const _hoisted_8 = {
  key: 0,
  class: "api-error"
}
const _hoisted_9 = { class: "actions" }

import { computed, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import { useModalStore } from "@/shared/stores/modal";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import AppFieldLabel from "@/shared/components/ui/AppFieldLabel.vue";

type ServiceOption = {
  id: string;
  title: string;
  subtitle: string;
};

type PackagePricing = {
  type: string;
  amount: number | string;
};

type Package = {
  name: string;
  highlight: string;
  features: string;
  pricing: PackagePricing[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'AddPackageModal',
  props: {
    onAdd: { type: Function }
  },
  setup(__props: any) {

const props = __props;

const modal = useModalStore();

const name = ref("");
const highlight = ref("");
const features = ref("");
const selectedServiceIds = ref<string[]>([]);
const pricing = ref<PackagePricing[]>([{ type: "month", amount: "" }]);

// Error messages for validation
const nameError = ref("");
const highlightError = ref("");
const featuresError = ref("");
const pricingError = ref("");

const isEditing = computed(() => false);

const availableServices: ServiceOption[] = [
  { id: "pest_control", title: "Pest Control", subtitle: "General pest control, inspections, and treatments." },
  { id: "termite", title: "Termite", subtitle: "Termite inspection, prevention, and remediation." },
  { id: "rodent", title: "Rodent", subtitle: "Rodent control, exclusion, and cleanup recommendations." },
  { id: "bed_bugs", title: "Bed Bugs", subtitle: "Assessment and targeted treatment plans." },
  { id: "mosquito", title: "Mosquito", subtitle: "Yard treatments and seasonal maintenance." },
];

const priceTypeOptions = [
  { title: "Daily", value: "day" },
  { title: "Weekly", value: "week" },
  { title: "Monthly", value: "month" },
  { title: "Yearly", value: "year" },
];

// Initialize form
// No editing functionality needed for create-only modal

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function generateId() {
  const base = slugify(name.value) || "package";
  return `${base}_${Date.now()}`;
}

function toggleService(serviceId: string, checked: boolean) {
  const next = new Set(selectedServiceIds.value);
  if (checked) next.add(serviceId);
  else next.delete(serviceId);
  selectedServiceIds.value = Array.from(next);
}

function addPricing() {
  pricing.value.push({ type: "month", amount: "" });
}

function removePricing(index: number) {
  if (pricing.value.length > 1) {
    pricing.value.splice(index, 1);
  }
}

function handleSubmit() {
  // Clear previous errors
  nameError.value = "";
  highlightError.value = "";
  featuresError.value = "";
  pricingError.value = "";

  const pkgName = name.value.trim();
  if (!pkgName) {
    nameError.value = "Package name is required";
    return;
  }

  const pkgHighlight = highlight.value.trim();
  if (!pkgHighlight) {
    highlightError.value = "Package highlight is required";
    return;
  }

  const pkgFeatures = features.value.trim();
  if (!pkgFeatures) {
    featuresError.value = "Package features are required";
    return;
  }

  // Validate pricing tiers: ensure each has a type and numeric amount > 0
  const pricingMsgs: string[] = [];
  const validatedPricing = [] as PackagePricing[];
  pricing.value.forEach((p, idx) => {
    const row = idx + 1;
    // normalize type value: if item object provided, use its value
    const typeVal = (p as any)?.type ?? p.type;
    if (!typeVal) {
      pricingMsgs.push(`Pricing tier ${row}: type is required.`);
    }
    const amtRaw = p.amount === "" ? null : Number(p.amount);
    if (amtRaw === null || Number.isNaN(amtRaw) || amtRaw <= 0) {
      pricingMsgs.push(`Pricing tier ${row}: amount is required and must be greater than 0.`);
    }
    if (typeVal && amtRaw !== null && !Number.isNaN(amtRaw) && amtRaw > 0) {
      validatedPricing.push({ type: typeVal, amount: amtRaw });
    }
  });

  if (pricingMsgs.length > 0) {
    pricingError.value = pricingMsgs.join(' ');
    return;
  }

  const pkg: Package = {
    name: pkgName,
    highlight: pkgHighlight,
    features: pkgFeatures,
    pricing: validatedPricing
  };

  props.onAdd?.(pkg);
  
  modal.close();
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_toolbar, {
      class: "modal-toolbar",
      density: "comfortable",
      flat: ""
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_v_btn, {
            icon: "",
            variant: "text",
            class: "back-btn",
            "aria-label": "Back",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(modal).close()))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, null, {
                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                  _createTextVNode("mdi-arrow-left", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }),
          _cache[10] || (_cache[10] = _createElementVNode("div", { class: "modal-title" }, "Add package", -1))
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_v_card_text, { class: "body py-6" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_form, {
          class: "form",
          onSubmit: _withModifiers(handleSubmit, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(AppTextField, {
              modelValue: name.value,
              "onUpdate:modelValue": [
                _cache[1] || (_cache[1] = ($event: any) => ((name).value = $event)),
                _cache[2] || (_cache[2] = ($event: any) => (nameError.value = ''))
              ],
              label: "Package name",
              placeholder: "e.g., Basic Pest Control",
              "error-messages": nameError.value
            }, null, 8, ["modelValue", "error-messages"]),
            _createVNode(AppTextArea, {
              modelValue: highlight.value,
              "onUpdate:modelValue": [
                _cache[3] || (_cache[3] = ($event: any) => ((highlight).value = $event)),
                _cache[4] || (_cache[4] = ($event: any) => (highlightError.value = ''))
              ],
              label: "Highlight",
              placeholder: "e.g., Exterior treatment + monthly follow-up",
              rows: "3",
              "error-messages": highlightError.value
            }, null, 8, ["modelValue", "error-messages"]),
            _createVNode(AppTextArea, {
              modelValue: features.value,
              "onUpdate:modelValue": [
                _cache[5] || (_cache[5] = ($event: any) => ((features).value = $event)),
                _cache[6] || (_cache[6] = ($event: any) => (featuresError.value = ''))
              ],
              label: "Features",
              placeholder: "e.g., Spray, Inspection, Warranty",
              hint: "Enter features separated by commas..",
              rows: "4",
              "error-messages": featuresError.value
            }, null, 8, ["modelValue", "error-messages"]),
            _createElementVNode("div", _hoisted_3, [
              _createVNode(AppFieldLabel, { label: "Pricing Tiers" })
            ]),
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pricing.value, (price, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "pricing-item"
                }, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createVNode(AppSelectField, {
                        modelValue: price.type,
                        "onUpdate:modelValue": [
                          ($event: any) => ((price.type) = $event),
                          _cache[7] || (_cache[7] = () => pricingError.value = '')
                        ],
                        items: priceTypeOptions,
                        clearable: false,
                        "hide-details": ""
                      }, null, 8, ["modelValue", "onUpdate:modelValue"])
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                      _createVNode(AppTextField, {
                        modelValue: price.amount,
                        "onUpdate:modelValue": [
                          ($event: any) => ((price.amount) = $event),
                          _cache[8] || (_cache[8] = () => pricingError.value = '')
                        ],
                        type: "number",
                        placeholder: "0.00",
                        prefix: "$",
                        "hide-details": ""
                      }, null, 8, ["modelValue", "onUpdate:modelValue"])
                    ]),
                    _createElementVNode("div", null, [
                      (pricing.value.length > 1)
                        ? (_openBlock(), _createBlock(_component_v_btn, {
                            key: 0,
                            size: "small",
                            variant: "text",
                            icon: "mdi-delete",
                            color: "error",
                            onClick: ($event: any) => (removePricing(index)),
                            class: "delete-btn"
                          }, null, 8, ["onClick"]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]))
              }), 128)),
              _createVNode(AppButtonFlat, {
                variant: "outlined",
                "prepend-icon": "mdi-plus",
                onClick: addPricing,
                class: "add-pricing-btn"
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" Add another pricing tier ", -1)
                ]))]),
                _: 1
              }),
              (pricingError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(pricingError.value), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(AppButtonFlat, {
                block: "",
                onClick: handleSubmit,
                "prepend-icon": "mdi-check",
                color: "primary"
              }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode(" Create ", -1)
                ]))]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})